/**
 * Performance monitoring utilities for hash map optimizations
 * Tracks performance improvements from O(n) to O(1) lookups
 */

interface PerformanceMetrics {
  operation: string;
  arraySize: number;
  findTime: number;
  mapTime: number;
  improvement: number;
  timestamp: number;
}

class PerformanceMonitor {
  private metrics: PerformanceMetrics[] = [];
  private readonly isEnabled = import.meta.env.DEV;

  /**
   * Benchmark array.find() vs Map lookup performance
   */
  benchmarkLookup<T>(
    array: T[],
    findFn: (item: T) => boolean,
    map: Map<string | number, T>,
    key: string | number,
    operation: string
  ): void {
    if (!this.isEnabled) return;

    const arraySize = array.length;

    // Benchmark array.find()
    const findStart = performance.now();
    const findResult = array.find(findFn);
    const findTime = performance.now() - findStart;

    // Benchmark Map lookup
    const mapStart = performance.now();
    const mapResult = map.get(key);
    const mapTime = performance.now() - mapStart;

    // Verify results are equivalent
    if (findResult !== mapResult) {
      console.warn(`Performance benchmark mismatch for ${operation}:`, {
        findResult,
        mapResult,
        key,
      });
      return;
    }

    const improvement = findTime / mapTime;
    const metric: PerformanceMetrics = {
      operation,
      arraySize,
      findTime,
      mapTime,
      improvement,
      timestamp: Date.now(),
    };

    this.metrics.push(metric);

    // Log significant improvements
    if (improvement > 10 && arraySize > 50) {
      console.log(`🚀 Hash map optimization: ${operation}`, {
        arraySize,
        'find() time': `${findTime.toFixed(3)}ms`,
        'map.get() time': `${mapTime.toFixed(3)}ms`,
        improvement: `${improvement.toFixed(1)}x faster`,
      });
    }
  }

  /**
   * Get performance metrics summary
   */
  getMetrics(): {
    totalOperations: number;
    averageImprovement: number;
    bestImprovement: PerformanceMetrics | null;
    recentMetrics: PerformanceMetrics[];
  } {
    if (this.metrics.length === 0) {
      return {
        totalOperations: 0,
        averageImprovement: 0,
        bestImprovement: null,
        recentMetrics: [],
      };
    }

    const totalImprovement = this.metrics.reduce((sum, m) => sum + m.improvement, 0);
    const averageImprovement = totalImprovement / this.metrics.length;
    const bestImprovement = this.metrics.reduce((best, current) =>
      current.improvement > best.improvement ? current : best
    );

    // Return last 10 metrics
    const recentMetrics = this.metrics.slice(-10);

    return {
      totalOperations: this.metrics.length,
      averageImprovement,
      bestImprovement,
      recentMetrics,
    };
  }

  /**
   * Clear all metrics
   */
  clear(): void {
    this.metrics = [];
  }

  /**
   * Export metrics for analysis
   */
  export(): PerformanceMetrics[] {
    return [...this.metrics];
  }
}

// Global performance monitor instance
export const performanceMonitor = new PerformanceMonitor();

/**
 * Web Vitals monitoring functions
 * These are lazy-loaded to avoid importing web-vitals in production bundles when not needed
 */

export function reportWebVitals(onMetric: (metric: unknown) => void): () => void {
  if (typeof window === 'undefined') return () => {};

  // Dynamic import to avoid bundling web-vitals unless actually used
  import('web-vitals')
    .then(({ onCLS, onINP, onFCP, onLCP, onTTFB }) => {
      // CLS - Cumulative Layout Shift
      onCLS(onMetric);

      // INP - Interaction to Next Paint
      onINP(onMetric);

      // FCP - First Contentful Paint
      onFCP(onMetric);

      // LCP - Largest Contentful Paint
      onLCP(onMetric);

      // TTFB - Time to First Byte
      onTTFB(onMetric);

      return undefined;
    })
    .catch(() => {
      // web-vitals failed to load, do nothing
    });

  // Return cleanup function (web-vitals handles cleanup automatically on page unload)
  return () => {};
}

export function measureLCP(onMetric: (metric: unknown) => void): () => void {
  if (typeof window === 'undefined') return () => {};

  // Dynamic import to avoid bundling web-vitals unless actually used
  import('web-vitals')
    .then(({ onLCP }) => {
      return onLCP(onMetric);
    })
    .catch(() => {
      // web-vitals failed to load, do nothing
    });

  // Return cleanup function (will be called even if web-vitals fails to load)
  return () => {};
}

/**
 * Computation cache for expensive operations
 * Simple LRU cache implementation
 */
export class ComputationCache<T = unknown> {
  private readonly cache = new Map<string, T>();
  private readonly maxSize: number;

  constructor(maxSize: number = 100) {
    this.maxSize = maxSize;
  }

  get(key: string): T | undefined {
    return this.cache.get(key);
  }

  set(key: string, value: T): void {
    // Remove oldest entry if cache is full
    if (this.cache.size >= this.maxSize) {
      const firstKey = this.cache.keys().next().value;
      if (firstKey) {
        this.cache.delete(firstKey);
      }
    }
    this.cache.set(key, value);
  }

  clear(): void {
    this.cache.clear();
  }

  size(): number {
    return this.cache.size;
  }
}

/**
 * Helper function to create benchmarked hash map lookups
 */
export function createBenchmarkedMap<T>(
  items: T[],
  getId: (item: T) => string | number,
  operation: string
): Map<string, T> {
  const map = new Map<string, T>();
  for (const item of items) {
    const id = getId(item);
    map.set(String(id), item);
  }

  // Add benchmarking wrapper if in development
  if (import.meta.env.DEV) {
    const originalGet = map.get.bind(map);
    map.get = (key: string) => {
      const start = performance.now();
      const result = originalGet(key);
      const time = performance.now() - start;

      // Log very slow lookups (potential performance issues)
      if (time > 1) {
        console.warn(`Slow map lookup for ${operation}: ${time.toFixed(3)}ms`);
      }

      return result;
    };
  }

  return map;
}
